import React from 'react';

/* Hand-drawn inline SVG icon set.

   Replaces the emoji previously used in the mobile tab bar and menus. Emoji
   render as full-colour bitmaps that differ per platform (Apple, Google, and
   Samsung all draw the same codepoint differently), which made the navigation
   look inconsistent and unfinished across the Android/iOS mix that makes up
   ~92% of traffic.

   These inherit `currentColor` and stroke weight, so a tab's active state is a
   colour change on the icon and label together. No dependency added: the whole
   set is smaller than any icon package's import cost.

   All icons share a 24x24 viewBox and 1.75 stroke so they sit on the same
   optical weight. */

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
};

// Stacked cards, slightly fanned — the flashcard deck.
export const IconCards = ({ className = '' }) => (
  <svg {...base} className={className}>
    <rect x="7" y="3.5" width="13" height="10" rx="2" transform="rotate(8 13.5 8.5)" />
    <rect x="3.5" y="8" width="13" height="12.5" rx="2" />
  </svg>
);

// Document with a checked line — a completed test paper.
export const IconExam = ({ className = '' }) => (
  <svg {...base} className={className}>
    <path d="M5.5 3.5h9l4.5 4.5v12a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 4 20V5a1.5 1.5 0 0 1 1.5-1.5Z" />
    <path d="M14 3.5V8h4.5" />
    <path d="m8.5 14.5 2 2 4-4" />
  </svg>
);

// Open book — the study guide.
export const IconGuide = ({ className = '' }) => (
  <svg {...base} className={className}>
    <path d="M12 7.5C10.5 6 8.5 5.5 4 5.5v12c4.5 0 6.5.5 8 2 1.5-1.5 3.5-2 8-2v-12c-4.5 0-6.5.5-8 2Z" />
    <path d="M12 7.5v12" />
  </svg>
);

// Lightning bolt — Quick-Fire.
export const IconBolt = ({ className = '' }) => (
  <svg {...base} className={className}>
    <path d="M13.5 2.5 4.5 13.5h6l-.5 8 9-11h-6l.5-8Z" />
  </svg>
);

// Horizontal rules — the overflow menu.
export const IconMore = ({ className = '' }) => (
  <svg {...base} className={className}>
    <path d="M4 7h16M4 12h16M4 17h11" />
  </svg>
);

// Crescent moon — switch to dark.
export const IconMoon = ({ className = '' }) => (
  <svg {...base} className={className}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </svg>
);

// Sun — switch to light.
export const IconSun = ({ className = '' }) => (
  <svg {...base} className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  </svg>
);

// Padlock — locked premium content.
export const IconLock = ({ className = '' }) => (
  <svg {...base} className={className}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

// Close.
export const IconClose = ({ className = '' }) => (
  <svg {...base} className={className}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);
